import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer, CellMeasurerCache, InfiniteLoader, List } from 'react-virtualized';
import clsx from 'clsx';
import { ThreeDots } from 'react-loader-spinner';

import useBodyClicker from '../../hooks/useBodyClicker';
import RowRenderer from './RowRenderer';
import { Input } from '../common-inputs';
import Icon from '../../icons/Icon';
import { ICONS } from '../../icons';

import './CommonVirtualizedDropdown.scss';

const commonCellMeasurerCache = new CellMeasurerCache({
  minHeight: 40,
  fixedHeight: false,
  fixedWidth: true,
});

const CommonVirtualizedDropdown = ({
  value,
  options,
  onSelect,
  isError,
  disabled,
  maxWidth,
  maxHeight = '200px',
  placeholder,
  name,
  isCloseOnSelect = true,
  inputSize = 'sm',
  isReadOnly,
  tabIndex,
  autoComplete,

  onSearch,
  onLoadMore,
  onBlur,
  onFocus,
  isLoading,
  totalElements,
  renderContentItem,
  renderInput,

  cellMeasurerCache = commonCellMeasurerCache,
  isHighlighted,
  customListItemStyle,

  classes = { root: '', input: '', menu: '', item: '' },
}) => {
  const style = { maxWidth };

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const isDirty = useRef(false);
  const listRef = useRef(null);
  const dropdownRef = useBodyClicker(() => handleClickOutside());

  const scrollToIndex = useCallback((index) => {
    if (listRef.current) {
      listRef.current.scrollToRow(index);
    }
  }, []);

  const handleResetSearch = useCallback(() => {
    if (onSearch && isDirty.current) {
      onSearch('');
    }

    isDirty.current = false;
  }, [onSearch]);

  const handleSelectOption = useCallback(
    (option) => {
      setSearchQuery('');
      setIsOpen(!isCloseOnSelect);

      if (onSelect) {
        onSelect(option);
      }

      handleResetSearch();
    },
    [handleResetSearch, onSelect, isCloseOnSelect],
  );

  const handleSearch = useCallback(
    (newSearch) => {
      isDirty.current = true;
      setSearchQuery(newSearch);

      if (onSearch) {
        onSearch(newSearch);
      }
    },
    [onSearch],
  );

  const handleClickOutside = useCallback(() => {
    setSearchQuery('');
    setIsOpen(false);

    handleResetSearch();
  }, [handleResetSearch]);

  const handleOpenDropdown = useCallback(() => {
    !disabled ? setIsOpen(true) : null;
  }, [disabled]);

  const dropdownHeight = useMemo(() => {
    const numElements = options.length;
    const paddingTopBottom = 7;
    const elementHeight = 40;

    return numElements < 5 ? `${paddingTopBottom * 2 + numElements * elementHeight}px` : maxHeight;
  }, [maxHeight, options]);

  const optionsRowRenderer = ({ index, key, style, parent }) => {
    return (
      <RowRenderer
        index={index}
        key={key}
        style={style}
        parent={parent}
        options={options}
        handleSelectOption={handleSelectOption}
        cellMeasurerCache={cellMeasurerCache}
        renderContentItem={renderContentItem}
        isHighlighted={isHighlighted}
        searchQuery={searchQuery}
        customListItemStyle={customListItemStyle}
        itemClassName={classes.item}
      />
    );
  };

  const isRowLoaded = useCallback(({ index }) => !!options[index], [options]);

  const loadMoreRows = useCallback(() => {
    if (!isLoading && !!onLoadMore && options.length !== totalElements) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [isLoading, onLoadMore, options.length, totalElements]);

  useEffect(() => {
    if (!!onLoadMore && page !== 1 && !isLoading) {
      onLoadMore({ page, search: searchQuery });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, onLoadMore]);

  useEffect(() => {
    setPage(1);
    scrollToIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <div className={clsx('common-virtualized-dropdown', classes.root)} ref={dropdownRef} style={style}>
      {renderInput ? (
        renderInput({
          handleOpenDropdown,
          value,
          disabled,
          isReadOnly,
          handleSearch,
          inputSize,
          isError,
          placeholder,
          onBlur,
          onFocus,
          name,
          isOpen,
          searchQuery,
          isLoading,
          onSelect: handleSelectOption,
        })
      ) : (
        <Input
          onClick={handleOpenDropdown}
          value={isDirty.current ? searchQuery : value?.label || ''}
          debounce
          disabled={disabled}
          isReadOnly={isReadOnly}
          onChange={handleSearch}
          size={inputSize}
          isError={isError}
          placeholder={placeholder}
          onBlur={onBlur}
          onFocus={onFocus}
          name={name}
          extraInputClass={classes.input}
          autoComplete={autoComplete}
          tabIndex={tabIndex}
          RightIcon={
            isLoading ? (
              <ThreeDots height={10} width={20} />
            ) : (
              <Icon
                icon={ICONS.CaretUp}
                disableCommonStyles
                className={clsx(
                  'common-virtualized-dropdown_icon',
                  isOpen ? 'common-virtualized-dropdown_icon--up' : 'common-virtualized-dropdown_icon--down',
                )}
              />
            )
          }
        />
      )}

      {isOpen ? (
        <div className={clsx('common-virtualized-dropdown_menu', classes.menu)} style={{ height: dropdownHeight }}>
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isRowLoaded={isRowLoaded}
                loadMoreRows={loadMoreRows}
                rowCount={options.length + 1}
                threshold={1}>
                {({ onRowsRendered, registerChild }) => (
                  <List
                    ref={(ref) => {
                      listRef.current = ref;
                      registerChild(ref);
                    }}
                    onRowsRendered={onRowsRendered}
                    rowRenderer={optionsRowRenderer}
                    rowCount={options.length}
                    rowHeight={cellMeasurerCache.rowHeight}
                    deferredMeasurementCache={cellMeasurerCache}
                    width={width}
                    height={height}
                  />
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        </div>
      ) : null}
    </div>
  );
};

export default memo(CommonVirtualizedDropdown);
